import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Pause, Play } from "lucide-react";
import { reviewApi, type LiveBoard as Board, type LiveRow } from "../../../api/clock";
import { venueApi } from "../../../api/resources";
import type { VenueAdmin } from "../../../api/types";
import { LoadState } from "../shared";

export const CHIP: Record<string, [string, string]> = {
  NOT_CLOCKED_IN: ["Not Clocked In", "border border-white/15 bg-white/[0.06] text-zinc-200"],
  PENDING_GPS: ["Pending GPS", "border border-sky-400/35 bg-sky-400/10 text-sky-200"],
  CLOCKED_IN: ["Clocked In", "border border-emerald-400/35 bg-emerald-400/10 text-emerald-200"],
  PENDING_CLOCK_OUT_GPS: ["Pending Clock-Out GPS", "border border-sky-400/35 bg-sky-400/10 text-sky-200"],
  COMPLETED: ["Completed", "border border-white/15 bg-white/[0.06] text-white"],
  NEEDS_REVIEW: ["Needs Review", "border border-amber-400/40 bg-amber-400/10 text-amber-200"],
};
type Filter = "" | "clocked_in" | "pending_gps" | "missing_out" | "rejected_location" | "open_exceptions";
const MATCH: Record<Exclude<Filter, "">, (r: LiveRow) => boolean> = {
  clocked_in: (r) => r.status === "CLOCKED_IN" || r.status === "PENDING_CLOCK_OUT_GPS",
  pending_gps: (r) => !!r.pending_expires_at,
  missing_out: (r) => r.review_flags.includes("MISSING_OUT"),
  rejected_location: (r) => !!r.gps && !r.gps.passed,
  open_exceptions: (r) => r.exceptions.length > 0,
};
const delay = (s: number | null) => (s == null ? "—" : s < 60 ? `${Math.round(s)}s` : `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`);

function Countdown({ to }: { to: string }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  const s = Math.max(0, Math.round((new Date(to).getTime() - now) / 1000));
  return <span className="font-mono">{Math.floor(s / 60)}:{String(s % 60).padStart(2, "0")}</span>;
}

function Row({ r, hl }: { r: LiveRow; hl: number }) {
  const [label, tone] = CHIP[r.status] ?? [r.status, "bg-white/10"];
  const td = "px-3 py-3 text-xs text-zinc-300 align-top";
  return (
    <tr data-testid={`live-row-${r.assignment_id}`} className="border-t border-white/5">
      <td className={td}><p className="font-semibold text-white">{r.employee_name}</p><p className="text-zinc-500">{r.role ?? ""}</p></td>
      <td className={td}><span data-testid={`live-status-${r.assignment_id}`} className={`rounded-sm px-2 py-1 text-[10px] font-bold uppercase tracking-wider ${tone}`}>{label}</span>{r.on_break && <span data-testid={`live-on-break-${r.assignment_id}`} className="ml-1 rounded-sm bg-amber-400/20 px-2 py-1 text-[10px] font-bold uppercase text-amber-200">On break</span>}
        {r.running_late && <span data-testid={`live-running-late-${r.assignment_id}`} className="ml-1 rounded-sm bg-amber-400/20 px-2 py-1 text-[10px] font-bold uppercase text-amber-200">Running late{r.late_minutes ? ` (${r.late_minutes} min)` : ""}</span>}
        {r.employee_response === "DECLINED" && <span data-testid={`live-declined-${r.assignment_id}`} className="ml-1 rounded-sm bg-signal/20 px-2 py-1 text-[10px] font-bold uppercase text-signal">Declined</span>}
        {r.pending_expires_at && <p className="mt-1 text-[11px] text-sky-300">link expires in <Countdown to={r.pending_expires_at} /></p>}</td>
      <td className={td}>{r.shift_time}</td>
      <td className={td}>SMS {r.in_sms || "—"}<br /><span className="text-zinc-500">GPS {r.in_gps || "—"}</span></td>
      <td className={td}>SMS {r.out_sms || "—"}<br /><span className="text-zinc-500">GPS {r.out_gps || "—"}</span></td>
      <td className={`${td} ${r.delay_highlight ? "font-bold text-warn" : ""}`} data-testid={`live-delay-${r.assignment_id}`} title={`Highlighted above ${hl} min`}>
        IN {delay(r.delay_in_sec)}<br />OUT {delay(r.delay_out_sec)}
      </td>
      <td className={td}>{r.gps ? <span data-testid={`live-gps-${r.assignment_id}`} className={`rounded-sm px-2 py-0.5 text-[10px] font-bold ${r.gps.passed ? "bg-emerald-400/15 text-emerald-300" : "bg-signal/20 text-red-200"}`}>{r.gps.passed ? "PASS" : `FAIL · ${r.gps.code}`}</span> : "—"}</td>
      <td className={td}>
        {r.exceptions.map((e) => <Link key={e.id} to={`/admin/corrections?open=exceptions:${e.id}`} className="block text-signal hover:text-white" data-testid={`live-exception-link-${e.id}`}>{e.code}</Link>)}
        {r.session_id && <Link to={`/admin/timesheets?employee_id=${r.employee_id}`} className="block text-zinc-400 hover:text-white" data-testid={`live-session-link-${r.assignment_id}`}>Session →</Link>}
      </td>
    </tr>
  );
}

export function LiveBoard() {
  const [date, setDate] = useState("");
  const [venue, setVenue] = useState("");
  const [filter, setFilter] = useState<Filter>("");
  const [paused, setPaused] = useState(false);
  const [data, setData] = useState<Board | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [venues, setVenues] = useState<VenueAdmin[]>([]);
  useEffect(() => { venueApi.list(undefined, "ACTIVE").then(setVenues).catch(() => setVenues([])); }, []);
  const load = useCallback(() => reviewApi.live({ date: date || undefined, venue_id: venue || undefined }).then((d) => { setData(d); setError(null); }).catch((e) => setError(String(e.message ?? e))), [date, venue]);
  useEffect(() => {
    load();
    if (paused) return;
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load, paused]);
  const c = data?.counters;
  return (
    <div data-testid="live-board">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="live-date-filter" className="h-10 rounded-sm border border-white/15 bg-surface px-3 text-xs text-white" />
        <select value={venue} onChange={(e) => setVenue(e.target.value)} data-testid="live-venue-filter" className="h-10 rounded-sm border border-white/15 bg-surface px-3 text-xs text-white">
          <option value="">All active venues</option>{venues.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
        </select>
        <span className="text-xs text-zinc-500">{date ? "" : "Today (each venue's local date)"}</span>
        <span data-testid="live-last-updated" className="ml-auto text-xs text-zinc-500">Last updated {data ? new Date(data.generated_at).toLocaleTimeString() : "—"}</span>
        <button type="button" onClick={() => setPaused((p) => !p)} data-testid="live-pause-toggle" className="inline-flex items-center gap-1 rounded-sm border border-white/15 px-3 py-2 text-xs text-zinc-200 hover:border-white/40">
          {paused ? <><Play className="h-3.5 w-3.5" /> Resume</> : <><Pause className="h-3.5 w-3.5" /> Pause</>} auto-refresh (15s)
        </button>
      </div>
      {c && (
        <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-5">
          {([["clocked_in", "Clocked in now"], ["pending_gps", "Pending GPS links"], ["missing_out", "Missing OUTs"], ["rejected_location", "Rejected location checks"], ["open_exceptions", "Open punch exceptions"]] as const).map(([k, l]) => (
            <button key={k} type="button" onClick={() => setFilter(filter === k ? "" : k)} data-testid={`live-counter-${k}`}
              className={`rounded-sm border p-4 text-left transition-colors ${filter === k ? "border-signal bg-signal/10 shadow-[0_0_24px_-8px_rgba(255,29,0,0.7)]" : "border-white/10 bg-surface hover:border-white/30"}`}>
              <p className="label-caps flex items-center gap-2 text-zinc-500">{k === "clocked_in" && c[k] > 0 && <span aria-hidden className="pulse-dot h-2 w-2 rounded-full bg-signal shadow-[0_0_8px_rgba(255,29,0,0.9)]" />}{l}</p>
              <p data-testid={`live-counter-value-${k}`} className="mt-1 font-display text-4xl font-bold text-white">{c[k]}</p>
              {k === "pending_gps" && c.pending_links.slice(0, 2).map((p, i) => <p key={i} className="truncate text-[11px] text-sky-300">{p.employee_name} {p.type} · <Countdown to={p.expires_at} /></p>)}
            </button>
          ))}
        </div>
      )}
      <LoadState error={error} loading={!data && !error} testId="live-load-state" />
      {data?.panels.length === 0 && <p data-testid="live-empty" className="text-sm text-zinc-400">No shifts scheduled for this date.</p>}
      <div className="space-y-6">
        {data?.panels.map((p) => {
          const rows = filter ? p.rows.filter(MATCH[filter]) : p.rows;
          return (
            <section key={p.venue_id} data-testid={`live-panel-${p.venue_id}`} className="rounded-sm border border-white/10 bg-surface">
              <header className="flex items-baseline justify-between border-b border-white/10 px-4 py-3">
                <h3 className="font-display text-xl font-bold uppercase text-white">{p.venue_name}</h3>
                <span className="text-xs text-zinc-500">{p.date} · {p.timezone} · {rows.length} shown</span>
              </header>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead><tr className="label-caps text-zinc-500">{["Employee", "Status", "Shift", "Clock-in", "Clock-out", "GPS delay", "GPS", "Links"].map((h) => <th key={h} className="px-3 py-2 font-normal">{h}</th>)}</tr></thead>
                  <tbody>{rows.map((r) => <Row key={r.assignment_id} r={r} hl={data.delay_highlight_min} />)}</tbody>
                </table>
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}
